import React, { Suspense, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { useDispatch } from 'react-redux';

import {
  CheckIcon,
  CopyIcon,
  KebabHorizontalIcon,
  ShareAndroidIcon,
  XIcon
} from '@primer/octicons-react';
import { Octicon } from '@primer/react';
import capitalize from 'lodash/capitalize';
import debounce from 'lodash/debounce';
import get from 'lodash/get';

import {
  LTActionMenu,
  LTButton,
  LTCodeBlock,
  LTCopyToClipboard,
  LTDrawer,
  LTIconButton,
  LTModal,
  LTText,
  LTLoader
} from '@lambdatestincprivate/lt-components';
import { ShareModal } from '@lt-web-platform/test-page-components';
import { canPerform } from '../../utils/rbacPermissions';
import { getEnvVar } from '../../utils/env';

import DarkThemeIcon from '../../assets/dark-theme';
import LightThemeIcon from '../../assets/light-theme';

import { copyTextToClipboard } from '../../utils/dom';
import { getStatusIconAndClassPrefix } from '../../utils/statusOptions';
import {
  isRenderingInTestDrawerFlagEnabled,
  isSharingRoute,
  isTestADemoTest,
  truncateTextFromMiddle,
  getShareId,
} from '../../utils/utilityFunctions';
import {
  ABORT_TEST_STATUS_VALUES,
  ACCESSIBILITY_RESOLUTION,
  DELETE_TEST_STATUS_VALUES,
  JsonTheme,
  ModalType
} from './constants.js';

import LTTestHeader from './LTTestHeader';

import { useQueryParams } from '../../hooks/useQueryParams.js';
import { deleteTestAction } from '../../redux/thunk/deleteTest.js';
import { stopTestAction } from '../../redux/thunk/stopTest.js';
import styles from './index.module.css';
import { isMobileDevice } from '../../utils/testHelpers.js';
import { TestTypes } from '../../constants/test.js';
import { useMuteTest } from '../../hooks/useMuteTest.js';

const MarkAsBug = React.lazy(() => import('../Nuisance'));

const TestDetailTopHeader = ({
  env,
  automationMediaHost,
  additionalKebabMenuItems,
  kebabMenuOrder,
  testDetail,
  currentSelectedBuild,
  deleteTestApiUrl,
  onDeleteTest,
  stopTestApiUrl,
  onStopTest,
  primaryheaderActionsFromProduct,
  amplitudeFeatureFlags,
  orgPreferences,
  userInfo,
  timelineFilters,
  isSampleBuild,
  isHeaderBtnInjectedByOtherProduct,
  secondaryHeaderData,
  updateTestMuteStatus,
  middleContent,
  compact,
  hideShareableLink: hideShareableLinkProp,
  rbac
}) => {
  const rbacCanUpdate = canPerform(rbac, 'canUpdate');
  const rbacCanRead = canPerform(rbac, 'canRead');
  const rbacCanExecute = canPerform(rbac, 'canExecute');
  const rbacCanDelete = canPerform(rbac, 'canDelete');
  const [showModal, setShowModal] = useState(false);
  const [showShareModal, setShowShareModal] = useState(false);
  const [jsonTheme, setJsonTheme] = useState(JsonTheme.LIGHT);
  const [modalType, setModalType] = useState(ModalType.DELETE);
  const [anchorRef, setAnchorRef] = useState('');
  const [showRemark, setShowRemark] = useState(true);
  const [menuItem, setMenuItem] = useState([]);
  const [isCopied, setIsCopied] = useState(false);
  const [openBugDrawer, setOpenBugDrawer] = useState(false);

  const {
    statusLoading,
    getMuteModalMessage,
    getMuteFooterButtons
  } = useMuteTest({
    testName: testDetail?.name,
    os: testDetail?.os,
    osName: testDetail?.osName,
    osVersion: testDetail?.osVersion,
    resolution: testDetail?.resolution,
    browser: testDetail?.browser,
    browserVersion: testDetail?.version,
    modalType: modalType,
    isTestMuted: testDetail?.feature_flag?.isMuted,
    testId: testDetail?.id,
    onCloseModal: () => toggleModalVisiblity(),
    onMuteTest: (testId, isMuted) => {
      updateTestMuteStatus(testId, isMuted);
    },
    userInfo,
    automationApiHost: orgPreferences?.automationApiHost
  });

  const testIdCopyBtnRef = useRef();
  const testRemarkDrawerRef = useRef();

  const queryParams = useQueryParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const shouldRenderTestInDrawer = isRenderingInTestDrawerFlagEnabled(
    amplitudeFeatureFlags
  );
  const isNuisanceV2Enabled =
    get(amplitudeFeatureFlags, ['nuisance-sdk', 'variant', 'key'], 'off') ===
    'on';

  const isLtAccessibilityEnabled =
    get(
      amplitudeFeatureFlags,
      ['lt_accessibility_flag', 'variant', 'key'],
      'off'
    ) === 'on';

  useEffect(() => {
    if (isCopied) {
      setTimeout(() => {
        setIsCopied(false);
        if (testIdCopyBtnRef && testIdCopyBtnRef.current) {
          testIdCopyBtnRef.current.focus();
        }
      }, 1000);
    }
  }, [isCopied]);

  const shareBtnRef = useRef(null);

  let { id = '', status = '', feature_flag } = testDetail || {};

  const { isMuted: isTestMuted } = feature_flag || {};

  const { statusIconName = '' } = getStatusIconAndClassPrefix(status);

  const isSharedLinkRoute = isSharingRoute();

  const isPublicLink =
    getEnvVar('REACT_APP_HIDE_PUBLIC_LINKS') &&
    getEnvVar('REACT_APP_HIDE_PUBLIC_LINKS') === 'true';

  // hide share option for mobile resolution
  const mediaQuery = window.matchMedia('(max-width: 440px)');

  const hideShareableLink =
    hideShareableLinkProp ||
    isPublicLink ||
    isSharedLinkRoute ||
    isTestADemoTest(testDetail?.id) ||
    mediaQuery.matches ||
    orgPreferences?.isLoading ||
    orgPreferences?.error ||
    orgPreferences?.publicResourceSharingDisabled;

  const updateMenuItemRef = useRef(null);
  updateMenuItemRef.current = () => {
    const isMuteTestEnabled = get(
      amplitudeFeatureFlags,
      'ml_auto_mute_test',
      false
    );

    const _menuItem = [];

    if (
      rbacCanUpdate &&
      !isMobileDevice(testDetail) &&
      isMuteTestEnabled &&
      testDetail?.type !== TestTypes.CYPRESS &&
      testDetail?.status?.toLowerCase() !== 'running'
    ) {
      _menuItem.push({
        name: isTestMuted ? 'Unmute Test...' : 'Mute Test...',
        onClickHandler: () => {
          toggleModalVisiblity(ModalType.MUTE, true);
        },
        variant: 'default',
        id: isTestMuted ? 'unmute_test' : 'mute_test'
      });
    }

    if (window.innerWidth > ACCESSIBILITY_RESOLUTION && !isPublicLink && !isSharedLinkRoute) {
      _menuItem.push({
        name: 'Create an issue...',
        onClickHandler: (anchorRef) => {
          setOpenBugDrawer(true);
          setAnchorRef(anchorRef);
        },
        variant: 'default',
        id: 'create_an_issue'
      });
    }

    if (additionalKebabMenuItems?.length > 0) {
      _menuItem.push(...additionalKebabMenuItems);
    }

    if (
      rbacCanRead &&
      !hideShareableLink &&
      testDetail?.statusInd?.toLowerCase() !== 'running' &&
      testDetail?.statusInd?.toLowerCase() !== 'queued'
    ) {
      _menuItem.push({
        name: 'Share...',
        onClickHandler: () => {
          setShowShareModal(true);
        },
        variant: 'default',
        id: 'share'
      });
    }

    if (rbacCanExecute && ABORT_TEST_STATUS_VALUES.includes(capitalize(testDetail?.statusInd))) {
      _menuItem.push({
        name: 'Stop...',
        onClickHandler: (anchorRef) => {
          toggleModalVisiblity(ModalType.STOP, true);
          setAnchorRef(anchorRef);
        },
        variant: 'danger',
        id: 'stop_test'
      });
    }

    if (
      rbacCanDelete &&
      !isSharedLinkRoute &&
      !isTestADemoTest(testDetail?.id) &&
      DELETE_TEST_STATUS_VALUES.includes(capitalize(testDetail?.statusInd))
    ) {
      _menuItem.push({
        name: 'Delete...',
        onClickHandler: (anchorRef) => {
          toggleModalVisiblity(ModalType.DELETE, true);
          setAnchorRef(anchorRef);
        },
        variant: 'danger',
        id: 'delete_test'
      });
    }
    if (kebabMenuOrder?.length > 0) {
      const ordered = kebabMenuOrder
        .map((id) => _menuItem.find((item) => item.id === id))
        .filter(Boolean);
      setMenuItem(ordered);
    } else {
      setMenuItem(_menuItem);
    }
  };

  const updateMenuItem = useMemo(
    () =>
      debounce(() => {
        updateMenuItemRef.current?.();
      }, 500),
    []
  );

  // Cleanup debounce on unmount
  useEffect(() => {
    return () => {
      updateMenuItem.cancel();
    };
  }, [updateMenuItem]);

  useEffect(() => {
    window.addEventListener('resize', updateMenuItem);
    updateMenuItem();
    return () => {
      window.removeEventListener('resize', updateMenuItem);
    };
  }, [isTestMuted, testDetail?.statusInd, updateMenuItem, rbacCanUpdate, rbacCanRead, rbacCanExecute, rbacCanDelete]);

  const toggleTheme = () => {
    setJsonTheme(
      jsonTheme === JsonTheme.LIGHT ? JsonTheme.DARK : JsonTheme.LIGHT
    );
  };

  const renderCodeBlockHeaderActions = (reason) => {
    return (
      <div className={styles.actionWrapper}>
        <div className={styles.actionBar}>
          <LTCopyToClipboard textToCopy={reason} />
          <LTIconButton
            aria-label="Change theme"
            data-testid="test-detail-theme-toggle"
            icon={jsonTheme === JsonTheme.DARK ? LightThemeIcon : DarkThemeIcon}
            onClick={toggleTheme}
            size={LTIconButton.Size.SMALL}
            variant={LTIconButton.IconButtonVariants.INVISIBLE}
          />
        </div>
      </div>
    );
  };

  const renderCommonHeaderAction = () => {
    return (
      <div className={styles.buildHeaderActions}>
        {primaryheaderActionsFromProduct}
        {compact && !hideShareableLink && (
          <LTIconButton
            aria-label="Share test"
            data-testid="test-detail-share-btn"
            icon={ShareAndroidIcon}
            onClick={() => setShowShareModal(true)}
            size={LTIconButton.Size.MEDIUM}
            variant={LTIconButton.IconButtonVariants.DEFAULT}
          />
        )}
        {!compact && (
          <LTButton
            data-testid="test-detail-copy-id-btn"
            forwardRef={testIdCopyBtnRef}
            id="testIdCopyButton"
            onClick={() => {
              setIsCopied(true);
              copyTextToClipboard(id);
            }}
            size={LTButton.Size.MEDIUM}
            trailingIcon={
              <Octicon
                icon={isCopied ? CheckIcon : CopyIcon}
                size={14}
                color={isCopied ? 'success.bg' : 'fg.muted'}
              />
            }
            variant={LTButton.Variant.DEFAULT}
          >
            <span>Test ID</span>
          </LTButton>
        )}
        {primaryheaderActionsFromProduct && (
          <div
            aria-hidden="true"
            style={{
              width: 1,
              alignSelf: 'stretch',
              backgroundColor: '#d0d7de'
            }}
          />
        )}
        {menuItem?.length > 0 && (
        <div id="testActionMenu">
          <LTActionMenu
            anchorType={LTActionMenu.AnchorType.ICON}
            data-testid="test-detail-actions-menu"
            iconButtonVariant={LTActionMenu.ButtonVariant.DEFAULT}
            leadingIcon={KebabHorizontalIcon}
            options={[
              {
                list: menuItem
              }
            ]}
          />
        </div>
        )}
      </div>
    );
  };

  const renderModalChildren = (modalType) => {
    if (modalType === ModalType.MUTE) {
      return statusLoading ? (
        <LTLoader />
      ) : (
        <div className={styles.deleteModal} style={{ textAlign: 'center' }}>
          {getMuteModalMessage()}
        </div>
      );
    }
    const textToRender =
      modalType === ModalType.DELETE ? 'deleting' : 'stopping';
    return (
      <div className={styles.deleteModal}>
        You are {textToRender} the test. This action can't be undone
      </div>
    );
  };

  const renderModalFooter = (modalType) => {
    const successBtnTitle = modalType === ModalType.DELETE ? 'Delete' : 'Stop';
    return (
      <LTModal.ModalFooter
        footerElements={
          modalType === ModalType.MUTE
            ? getMuteFooterButtons()
            : [
                {
                  dataTestId: 'test-detail-modal-cancel-btn',
                  id: 'cancelButton',
                  isCloseButton: true,
                  onClick: () => {
                    toggleModalVisiblity(modalType, false);
                  },
                  text: 'Cancel',
                  type: 'button',
                  variant: LTButton.Variant.DEFAULT
                },
                {
                  dataTestId: 'test-detail-modal-action-btn',
                  id: `actionButton`,
                  isCloseButton: true,
                  onClick: (event) => {
                    if (modalType === ModalType.DELETE) {
                      handleDeleteTest();
                      toggleModalVisiblity(modalType, false);
                    } else {
                      stopTest();
                      toggleModalVisiblity(modalType, false);
                    }
                  },
                  text: successBtnTitle,
                  type: 'button',
                  variant: LTButton.Variant.DANGER
                }
              ]
        }
        returnFocusRef={anchorRef}
      />
    );
  };

  const toggleModalVisiblity = (modalType, show) => {
    setModalType(modalType);
    setShowModal(show);
  };

  const handleDeleteTest = () => {
    dispatch(deleteTestAction({ apiUrl: deleteTestApiUrl })).then((res) => {
      onDeleteTest(res);
    });
  };

  const stopTest = () => {
    dispatch(stopTestAction({ apiUrl: stopTestApiUrl })).then((res) => {
      onStopTest(res);
    });
  };

  const goToBuilds = () => {
    if (isSharedLinkRoute) {
      clearDefaultTestData();

      const shareId = getShareId();

      const urlToUse = `/share?shareId=${shareId}`;
      navigate(urlToUse);
    } else {
      if (!currentSelectedBuild) {
        navigate(`/build?pageType=build`);
      } else {
        navigate({
          pathname: '/build',
          search: '?pageType=build',
          state: {
            detail: currentSelectedBuild,
            timelineFilters: timelineFilters
          }
        });
      }
    }
  };

  const { reason = '', id: testId = '' } = testDetail || {};

  const getRemarks = () => {
    const remarks = [];
    if (reason && showRemark) {
      remarks.push(
        <div
          className={`${styles.remark} commandLogRemark`}
          id="testRemark"
          style={{
            borderRadius: '6px'
          }}
        >
          <div id="remarks">
            <LTText
              variant={LTText.TextVariant.SMALL_BOLD}
            >{`Test remark: `}</LTText>
            <LTText variant={LTText.TextVariant.SMALL_REGULAR}>
              {truncateTextFromMiddle(reason, 200)}
            </LTText>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
            {reason.length > 200 && (
              <LTButton
                data-testid="test-detail-expand-remark-btn"
                id="expandButton"
                onClick={() => testRemarkDrawerRef.current.openDrawer()}
                variant={LTButton.Variant.DEFAULT}
              >
                Expand Remark
              </LTButton>
            )}
            <LTIconButton
              ariaLabel="remove remark"
              data-testid="test-detail-remove-remark-btn"
              icon={XIcon}
              id="removeButton"
              onClick={() => setShowRemark(false)}
              size={LTIconButton.Size.SMALL}
              variant={LTIconButton.IconButtonVariants.INVISIBLE}
            />
          </div>
        </div>
      );
    }
    return remarks;
  };

  return (
    <>
      <LTTestHeader
        testDetail={testDetail}
        secondaryHeaderData={secondaryHeaderData}
        middleContent={middleContent}
        compact={compact}
        showHeaderBtn={!(isSharedLinkRoute || shouldRenderTestInDrawer)}
        statusIconSrc={statusIconName}
        headerActions={
          isSharedLinkRoute || isSampleBuild
            ? undefined
            : renderCommonHeaderAction()
        }
        isSampleBuild={isSampleBuild}
        isLtAccessibilityEnabled={isLtAccessibilityEnabled}
        isHeaderBtnInjectedByOtherProduct={isHeaderBtnInjectedByOtherProduct}
        handleHeaderBtnClick={goToBuilds}
        remarkList={getRemarks()}
      />
      {showModal && (
        <LTModal
          noPortal={shouldRenderTestInDrawer}
          id="testActionModal"
          title={
            modalType === ModalType.DELETE
              ? 'Delete Test'
              : modalType === ModalType.MUTE
              ? isTestMuted
                ? 'Unmute Test'
                : 'Mute Test'
              : 'Stop Test'
          }
          onClose={(event) => {
            toggleModalVisiblity(modalType, false);
          }}
          renderFooter={
            (modalType !== ModalType.MUTE || !statusLoading) &&
            renderModalFooter(modalType)
          }
          returnFocusRef={anchorRef}
        >
          {renderModalChildren(modalType)}
        </LTModal>
      )}
      {showShareModal && (
        <ShareModal
          env={env}
          btnRef={shareBtnRef}
          show={showShareModal}
          onModalClose={() => {
            setShowShareModal(false);
          }}
          userInfo={userInfo}
          testDetails={testDetail}
        />
      )}
      {openBugDrawer && (
        <Suspense fallback={<></>}>
          <MarkAsBug
            automationMediaHost={automationMediaHost}
            test_details={testDetail}
            open={openBugDrawer}
            setOpen={(isOpen) => setOpenBugDrawer(isOpen)}
            userInfo={userInfo}
            featureFlags={amplitudeFeatureFlags}
          />
        </Suspense>
      )}
      <LTDrawer
        title="Test Remark"
        ref={testRemarkDrawerRef}
        content={
          <LTCodeBlock
            dataWrapperStyle={{
              background: jsonTheme === JsonTheme.DARK ? '#0C0D0E' : '#fff',
              color: jsonTheme === JsonTheme.DARK ? '#fff' : '#000',
              wordBreak: 'break-all',
              whiteSpace: 'pre-wrap'
            }}
            headerActions={renderCodeBlockHeaderActions(reason)}
          >
            <div className={styles.reasonContainer}>{reason}</div>
          </LTCodeBlock>
        }
        width="80%"
      />
    </>
  );
};

export default TestDetailTopHeader;
